<template>
  <div class="grid-y flex-grow p-8 w-full settings-legal-mobile">
    <Loading :loading="loading">
      <div class="flex flex-col max-w-6xl">
        <div class="mb-8">
          <b class="text-18px leading-20px">Правовые документы</b>
          <div class="mt-2 text-sm text-muted max-w-4xl">
            Здесь хранится только номер и дата действующей редакции. Сам текст документа
            по-прежнему публикуется на сайте iWALY и через админку не редактируется.
            Опубликованная редакция не изменяется и не удаляется: исправление оформляется
            новой версией.
          </div>
        </div>

        <div v-if="error" class="mb-6 text-danger text-sm">
          {{ error }}
        </div>

        <div class="flex flex-col space-y-8">
          <div
            v-for="document in documents"
            :key="document.type"
            class="border border-gray-300 rounded-lg p-6 bg-white"
          >
            <div class="flex flex-row justify-between items-start flex-wrap">
              <div class="pr-8 mb-4">
                <b class="text-16px leading-18px">{{ document.title }}</b>
                <div class="mt-3 text-sm">
                  <span class="text-muted">Текущая версия:</span>
                  <b class="ml-2">{{ document.current.version }}</b>
                </div>
                <div class="mt-1 text-sm">
                  <span class="text-muted">Дата редакции:</span>
                  <b class="ml-2">{{ formatDate(document.current.revisionDate) }}</b>
                </div>
                <a
                  v-if="document.url"
                  :href="document.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-block mt-3 text-primary font-bold text-sm underline"
                >
                  Открыть опубликованный документ
                </a>
              </div>

              <div class="w-full lg:w-auto lg:min-w-160">
                <div class="text-sm font-bold mb-3">Сохранить новую редакцию</div>
                <div class="flex flex-row flex-wrap -mx-3 -my-2 items-end">
                  <div class="px-3 py-2 w-72">
                    <Input
                      label="Номер версии"
                      classes="bg-hint border-none"
                      placeholder="2026-08-28.1"
                      :disabled="!canEdit || savingType === document.type"
                      v-model="drafts[document.type].version"
                    />
                  </div>
                  <div class="px-3 py-2 w-64">
                    <Input
                      type="date"
                      label="Дата редакции"
                      classes="bg-hint border-none"
                      :disabled="!canEdit || savingType === document.type"
                      v-model="drafts[document.type].revisionDate"
                    />
                  </div>
                  <div class="px-3 py-2">
                    <button
                      class="btn btn-primary"
                      :disabled="!canEdit || savingType === document.type"
                      @click="publish(document)"
                    >
                      {{ savingType === document.type ? 'Сохраняю…' : 'Сохранить новую редакцию' }}
                    </button>
                  </div>
                </div>
                <div v-if="!canEdit" class="mt-2 text-xs text-muted">
                  Для изменения требуется право «Редактирование настроек».
                </div>
                <div class="mt-2 text-xs text-muted">
                  Формат версии: YYYY-MM-DD.N. Дата в номере версии должна совпадать с датой редакции.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Loading>
  </div>
</template>

<script>
import Input from '@/components/Input.vue';
import Loading from '@/components/Loading.vue';
import {
  legalDocuments,
  publishLegalDocumentRevision,
} from '@/api/settings';

export default {
  components: {
    Input,
    Loading,
  },
  data() {
    return {
      documents: [],
      drafts: {},
      canEdit: false,
      loading: false,
      savingType: null,
      error: '',
    };
  },
  methods: {
    async fetchDocuments() {
      this.loading = true;
      this.error = '';

      try {
        const response = await legalDocuments();
        this.documents = (response.data && response.data.documents) || [];
        this.canEdit = !!(response.data && response.data.canEdit);
        this.resetDrafts();
      } catch (error) {
        this.error = 'Не удалось загрузить текущие редакции правовых документов.';
      } finally {
        this.loading = false;
      }
    },
    resetDrafts() {
      const next = {};
      this.documents.forEach((document) => {
        const draft = this.defaultDraft(document.current);
        this.$set(next, document.type, draft);
      });
      this.drafts = next;
    },
    defaultDraft(current) {
      const today = this.localDate();
      let sequence = 1;

      const currentVersion = String((current && current.version) || '');
      if (currentVersion.startsWith(`${today}.`)) {
        const suffix = parseInt(currentVersion.slice(today.length + 1), 10);
        if (!Number.isNaN(suffix)) {
          sequence = suffix + 1;
        }
      }

      return {
        version: `${today}.${sequence}`,
        revisionDate: today,
      };
    },
    localDate() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    formatDate(value) {
      if (!value) return '—';
      const [year, month, day] = String(value).split('-');
      if (!year || !month || !day) return value;
      return `${day}.${month}.${year}`;
    },
    validationMessage(error) {
      const errors = error
        && error.response
        && error.response.data
        && error.response.data.errors;
      if (errors) {
        const first = Object.keys(errors)[0];
        const messages = errors[first];
        if (Array.isArray(messages) && messages.length) {
          return messages[0];
        }
      }
      return 'Не удалось сохранить новую редакцию.';
    },
    async publish(document) {
      if (!this.canEdit || this.savingType) return;

      const draft = this.drafts[document.type];
      if (!draft || !draft.version || !draft.revisionDate) {
        this.error = 'Укажите номер версии и дату редакции.';
        return;
      }

      const confirmed = window.confirm(
        `Опубликовать ${document.title}: версия ${draft.version} от ${this.formatDate(draft.revisionDate)}?\n\n`
        + 'Существующая редакция останется в истории и не сможет быть перезаписана.',
      );
      if (!confirmed) return;

      this.savingType = document.type;
      this.error = '';

      try {
        const response = await publishLegalDocumentRevision(document.type, {
          version: draft.version,
          revisionDate: draft.revisionDate,
        });

        const index = this.documents.findIndex((item) => item.type === document.type);
        if (index >= 0 && response.data) {
          this.$set(this.documents, index, response.data);
          this.$set(this.drafts, document.type, this.defaultDraft(response.data.current));
        }

        if (this.$toast && this.$toast.success) {
          this.$toast.success('Новая редакция правового документа сохранена');
        }
      } catch (error) {
        this.error = this.validationMessage(error);
        if (this.$toast && this.$toast.error) {
          this.$toast.error(this.error);
        }
      } finally {
        this.savingType = null;
      }
    },
  },
  mounted() {
    this.fetchDocuments();
  },
};
</script>
